/**
 * Gemini AI — Generare texte prognoză calitate aer
 * Apelează Gemini API pentru a genera paragrafe naturale
 * pe baza statisticilor meteo/poluanți.
 * Cache-uiește rezultatele în sessionStorage.
 * Include retry logic cu exponential backoff pentru rate-limiting (429).
 */

const GEMINI_API_KEY = 'AIzaSyCuZcikzAKrnNwXtdmCFwI9drxu0ak2tGw';
const GEMINI_MODEL = 'gemini-2.5-flash';
const GEMINI_URL = `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent?key=${GEMINI_API_KEY}`;

// Cache key prefix
const CACHE_PREFIX = 'aq_forecast_';

// Serialization queue — one request at a time to avoid rate-limiting
let requestQueue = Promise.resolve();

/**
 * Generate a forecast paragraph for a given day using Gemini AI.
 * Serialized: only one API call runs at a time.
 * @param {Object} stats - Per-pollutant statistics
 * @param {number} dayIdx - Day index
 * @param {string} dayLabel - Human-readable day label
 * @param {string} dateStr - Date string
 * @returns {Promise<string>} Generated paragraph or null
 */
export function generateForecastAI(stats, dayIdx, dayLabel, dateStr) {
  // Chain onto the queue so requests run one at a time
  const result = requestQueue.then(() =>
    _generateForecastAI(stats, dayIdx, dayLabel, dateStr)
  );
  // Update queue (don't let errors break the chain)
  requestQueue = result.catch(() => null);
  return result;
}

async function _generateForecastAI(stats, dayIdx, dayLabel, dateStr) {
  // Check cache first
  const cacheKey = `${CACHE_PREFIX}${dateStr}_${dayIdx}`;
  const cached = sessionStorage.getItem(cacheKey);
  if (cached) return cached;

  const prompt = buildPrompt(stats, dayLabel, dateStr);
  const body = JSON.stringify({
    contents: [{ parts: [{ text: prompt }] }],
    generationConfig: {
      temperature: 0.7,
      maxOutputTokens: 1024,
      topP: 0.9,
      thinkingConfig: { thinkingBudget: 0 },
    },
    safetySettings: [
      { category: 'HARM_CATEGORY_HARASSMENT', threshold: 'BLOCK_NONE' },
      { category: 'HARM_CATEGORY_HATE_SPEECH', threshold: 'BLOCK_NONE' },
      { category: 'HARM_CATEGORY_SEXUALLY_EXPLICIT', threshold: 'BLOCK_NONE' },
      { category: 'HARM_CATEGORY_DANGEROUS_CONTENT', threshold: 'BLOCK_NONE' },
    ],
  });

  // Retry with exponential backoff (max 3 attempts)
  const MAX_RETRIES = 3;
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      const response = await fetch(GEMINI_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      });

      if (response.status === 429) {
        // Rate limited — extract retry delay or use exponential backoff
        const retryAfter = parseRetryDelay(await response.text());
        const delay = retryAfter || (2000 * Math.pow(2, attempt)); // 2s, 4s, 8s
        console.info(`Gemini: rate limited, retrying in ${Math.round(delay / 1000)}s (attempt ${attempt + 1}/${MAX_RETRIES})`);
        await sleep(delay);
        continue;
      }

      if (!response.ok) {
        console.warn('Gemini API error:', response.status);
        return null;
      }

      const data = await response.json();
      const candidate = data?.candidates?.[0];
      const text = candidate?.content?.parts?.[0]?.text?.trim();
      const finishReason = candidate?.finishReason;

      // Only cache complete responses (STOP = natural end)
      // Reject truncated responses (MAX_TOKENS)
      if (text && finishReason === 'STOP') {
        sessionStorage.setItem(cacheKey, text);
        // Small delay before next queued request to be gentle on rate limits
        await sleep(1500);
        return text;
      } else if (text && finishReason === 'MAX_TOKENS') {
        console.info('Gemini: response truncated, discarding');
        return null;
      }

      return null;
    } catch (err) {
      console.warn('Gemini API call failed:', err);
      return null;
    }
  }

  console.warn('Gemini: max retries exceeded');
  return null;
}

/**
 * Parse retry delay from Gemini error response.
 */
function parseRetryDelay(responseText) {
  try {
    const data = JSON.parse(responseText);
    // Look for retryDelay in error details
    const details = data?.error?.details || [];
    for (const detail of details) {
      if (detail.retryDelay) {
        // Parse "42s" or "1.5s" format
        const seconds = parseFloat(detail.retryDelay);
        if (!isNaN(seconds)) return seconds * 1000;
      }
    }
  } catch {
    // Not JSON, try regex
    const match = responseText.match(/retry\s+in\s+([\d.]+)s/i);
    if (match) return parseFloat(match[1]) * 1000;
  }
  return null;
}

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Build the prompt for Gemini based on pollutant statistics.
 */
function buildPrompt(stats, dayLabel, dateStr) {
  const lines = [];

  const pollutants = [
    { key: 'pm25', name: 'PM2.5', unit: 'µg/m³' },
    { key: 'pm10', name: 'PM10', unit: 'µg/m³' },
    { key: 'no2', name: 'NO₂', unit: 'µg/m³' },
    { key: 'o3', name: 'O₃ (ozon)', unit: 'µg/m³' },
    { key: 'so2', name: 'SO₂', unit: 'µg/m³' },
    { key: 'co', name: 'CO', unit: 'µg/m³' },
  ];

  for (const p of pollutants) {
    const s = stats[p.key];
    if (s && s.count > 0) {
      const catLabel = s.cat?.label || 'N/A';
      lines.push(`• ${p.name}: medie ${s.avg.toFixed(1)} ${p.unit}, max ${s.max.toFixed(1)}, min ${s.min.toFixed(1)} — categorie: ${catLabel}`);
    }
  }

  const statsBlock = lines.join('\n');

  return `Ești un meteorolog specializat în calitatea aerului din România. 
Scrie un paragraf scurt și informativ (3-4 propoziții, maxim 80 de cuvinte) despre prognoza calității aerului pentru ${dayLabel} (${dateStr}).

Datele prognozate (medii naționale pe baza modelului CAMS/ECMWF):
${statsBlock}

Reguli:
- Scrie în limba română, într-un stil clar și profesional, accesibil publicului larg
- Menționează poluanții relevanți cu valorile lor
- Include un sfat practic de sănătate adecvat nivelului de poluare
- NU folosi emoji, NU folosi asteriscuri sau bold, NU folosi titluri
- NU folosi formulări generice — fii specific pe baza datelor
- Dacă ozonul (O₃) depășește 100 µg/m³, menționează-l explicit ca factor de risc
- Variază formularea — nu începe mereu cu "Calitatea aerului"
- Răspunde DOAR cu paragraful, fără introducere sau explicații suplimentare`;
}
